import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, GripVertical, MoreHorizontal, Radio, X } from 'lucide-react';
import type { Track } from '@pujolisique/shared';
import { artUrl } from '../api/client';
import { usePersonInfo } from '../api/hooks';
import { useDominantColor } from '../lib/color';
import { artistLine, fmtTime, plural, qualityLabel } from '../lib/format';
import { currentTrack, usePlayer, type PanelTab } from '../player/store';
import { Cover } from './Cover';
import { Lyrics } from './Lyrics';
import { FavButton, Seekbar, Transport, Volume } from './PlayerBar';
import { Bars } from './TrackList';
import { useTrackMenu } from './trackActions';

const TABS: { id: PanelTab; label: string }[] = [
  { id: 'queue', label: 'À suivre' },
  { id: 'lyrics', label: 'Paroles' },
  { id: 'info', label: 'Infos' },
];

export function NowPlaying() {
  const open = usePlayer((s) => s.nowPlayingOpen);
  const t = usePlayer(currentTrack);
  const tab = usePlayer((s) => s.panelTab);
  const { openNowPlaying, setPanelTab } = usePlayer.getState();
  const source = usePlayer((s) => s.source);
  const wash = useDominantColor(t?.cover ? artUrl(t.cover, 128) : undefined);
  const menu = useTrackMenu();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && openNowPlaying(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, openNowPlaying]);

  if (!open || !t) return null;

  return (
    <div className="np" style={{ ['--wash' as string]: wash }} role="dialog" aria-modal="true" aria-label="Lecteur plein écran">
      <div className="np__bg" aria-hidden />
      <header className="np__top">
        <button className="icon-btn" onClick={() => openNowPlaying(false)} aria-label="Réduire le lecteur">
          <ChevronDown size={24} />
        </button>
        <span className="np__source">{source ? `Lecture : ${source}` : 'En lecture'}</span>
        <button className="icon-btn" onClick={(e) => menu(t, e.currentTarget.getBoundingClientRect())} aria-label="Plus d’actions">
          <MoreHorizontal size={22} />
        </button>
      </header>

      <div className="np__body">
        <section className="np__stage">
          <Cover art={t.cover} name={t.album ?? t.title} size={1024} className="np__art" eager />
          <div className="np__titles">
            <div>
              <h2 className="np__title">{t.title}</h2>
              <p className="np__artist">
                {t.artists.map((a, i) => (
                  <span key={a.id || a.name}>
                    {i > 0 && ', '}
                    {a.id ? (
                      <Link to={`/artist/${a.id}`} onClick={() => openNowPlaying(false)}>
                        {a.name}
                      </Link>
                    ) : (
                      a.name
                    )}
                  </span>
                ))}
              </p>
              {t.composers.length > 0 && (
                <p className="np__composer">
                  Composé par{' '}
                  {t.composers.map((c, i) => (
                    <span key={c.id}>
                      {i > 0 && ', '}
                      {c.id.startsWith('name:') ? (
                        c.name
                      ) : (
                        <Link to={`/composer/${c.id}`} onClick={() => openNowPlaying(false)}>
                          {c.name}
                        </Link>
                      )}
                    </span>
                  ))}
                </p>
              )}
            </div>
            <FavButton id={t.id} size={24} />
          </div>
          <Seekbar />
          <Transport big />
          <div className="np__volume">
            <Volume />
          </div>
        </section>

        <section className="np__panel">
          <div className="tabs" role="tablist">
            {TABS.map((x) => (
              <button key={x.id} role="tab" aria-selected={tab === x.id} className={`tab ${tab === x.id ? 'is-active' : ''}`} onClick={() => setPanelTab(x.id)}>
                {x.label}
              </button>
            ))}
          </div>
          <div className="np__panelbody">
            {tab === 'queue' && <Queue />}
            {tab === 'lyrics' && <Lyrics track={t} />}
            {tab === 'info' && <TrackInfo track={t} />}
          </div>
        </section>
      </div>
    </div>
  );
}

function Queue() {
  const queue = usePlayer((s) => s.queue);
  const index = usePlayer((s) => s.index);
  const playing = usePlayer((s) => s.playing);
  const radio = usePlayer((s) => s.radio);
  const { jump, removeAt, move, clearUpcoming, toggleRadio } = usePlayer.getState();
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const upcoming = queue.length - index - 1;

  const row = (t: Track, i: number) => (
    <li
      key={`${t.id}-${i}`}
      className={`qrow ${i === index ? 'is-current' : ''} ${i < index ? 'is-past' : ''}`}
      draggable={i !== index}
      onDragStart={() => setDragFrom(i)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => {
        if (dragFrom !== null) move(dragFrom, i);
        setDragFrom(null);
      }}
    >
      <span className="qrow__grip" aria-hidden>
        {i === index && playing ? <Bars /> : <GripVertical size={14} />}
      </span>
      <button className="qrow__main" onClick={() => jump(i)}>
        <Cover art={t.cover} name={t.album ?? t.title} size={64} />
        <span className="qrow__text">
          <span className="qrow__title">{t.title}</span>
          <span className="qrow__sub">{artistLine(t)}</span>
        </span>
      </button>
      <span className="qrow__dur">{fmtTime(t.duration)}</span>
      {i !== index ? (
        <button className="icon-btn" onClick={() => removeAt(i)} aria-label={`Retirer ${t.title} de la file`}>
          <X size={16} />
        </button>
      ) : (
        <span className="qrow__spacer" />
      )}
    </li>
  );

  return (
    <div className="queue">
      <ol className="queue__list">{queue.map((t, i) => (i >= Math.max(0, index - 3) ? row(t, i) : null))}</ol>
      <div className="queue__foot">
        <span>{upcoming > 0 ? `${plural(upcoming, 'titre', 'titres')} à suivre` : radio ? 'La radio prend le relais' : 'Fin de la file'}</span>
        <span className="queue__actions">
          <button
            className={`chip-toggle ${radio ? 'is-on' : ''}`}
            onClick={toggleRadio}
            aria-pressed={radio}
            title="Quand la file se termine, la lecture continue avec des titres proches"
          >
            <Radio size={14} /> Radio infinie
          </button>
          {upcoming > 0 && (
            <button className="btn btn--ghost" onClick={clearUpcoming}>
              Vider la suite
            </button>
          )}
        </span>
      </div>
    </div>
  );
}

function TrackInfo({ track: t }: { track: Track }) {
  const composer = t.composers.find((c) => !c.id.startsWith('name:'));
  const info = usePersonInfo('composer', composer?.id ?? '', composer?.name);
  const rows: [string, React.ReactNode][] = [];
  if (t.album) rows.push(['Album', t.albumId ? <Link to={`/album/${t.albumId}`}>{t.album}</Link> : t.album]);
  if (t.year) rows.push(['Année', t.year]);
  if (t.genres?.length) rows.push(['Genre', t.genres.join(', ')]);
  if (t.trackNumber) rows.push(['Piste', t.discNumber && t.discNumber > 1 ? `${t.trackNumber} (disque ${t.discNumber})` : t.trackNumber]);
  const q = qualityLabel(t);
  if (q) rows.push(['Qualité', q]);
  if (t.playCount) rows.push(['Écoutes', t.playCount]);
  if (t.path) rows.push(['Fichier', <span className="mono-path">{t.path}</span>]);
  return (
    <div className="info">
      <dl className="info__list">
        {rows.map(([k, v]) => (
          <div key={k} className="info__row">
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {composer && info.data?.info?.summary && (
        <article className="info__bio">
          <h3>{composer.name}</h3>
          {info.data.info.description && <p className="info__desc">{info.data.info.description}</p>}
          <p>{info.data.info.summary}</p>
          {info.data.info.url && (
            <a href={info.data.info.url} target="_blank" rel="noreferrer">
              Lire sur {info.data.info.source}
            </a>
          )}
        </article>
      )}
    </div>
  );
}
